import { Alpine } from '../core/alpine-core.js';
import { unwrapApiEntity } from '../core/api-entity.js';
import { apiFetch } from './api.js';
import { DEFAULT_PLAYER_AVATAR, playerAvatarUrl } from './player-avatar-url.js';
Alpine.data('userProfile', () => ({

    session: Alpine.store('session'),
    theme: Alpine.store('theme'),

    /** Stable portrait URL — avoids re-binding `:src` on every Alpine tick. */
    avatarSrc: DEFAULT_PLAYER_AVATAR,

    // UI state
    open: false,            // avatar dropdown
    previewOpen: false,     // profile view modal
    profileOpen: false,     // profile edit modal
    resetOpen: false,       // reset password modal

    matches: [],
    saving: false,
    resetting: false,

    // Edit draft (never mutate session.me directly)
    form: { fullName: '', nickname: '', email: '', phone: '', status: 'active', imageUrl: '' },
    imageFile: null,
    imagePreview: null,

    // ---------- Derived helpers ----------
    get authed() { return !!this.session?.token && !!this.session?.me; },
    get me() { return this.session?.me || null; },
    get player() { return this.me ?? null; },

    syncAvatarSrc() {
        this.avatarSrc = playerAvatarUrl(this.player);
    },
    avatar() {
        return this.avatarSrc;
    },
    accountTypeLabel() {
        const role = String(this.me?.role || '').trim();
        if (!role) return 'Account';
        return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase() + ' account';
    },
    teamLogo() {
        const t = this.me?.team;
        return t?.logoUrl || t?.logo || '';
    },
    joinedDate() {
        const iso = this.me?.createdAt;
        try {
            return iso ? new Date(iso).toLocaleDateString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Bangkok'
            }) : '—';
        } catch { return '—'; }
    },

    // ---------- Lifecycle ----------
    async init() {
        this.syncAvatarSrc();
        this.$watch('session.me', () => this.syncAvatarSrc());
        if (!this.session?.me) {
            await this.fetchMe(); // hydrate once if needed
        }
        this.syncAvatarSrc();
    },

    // ---------- Openers ----------
    openPreview() { this.open = false; this.previewOpen = true; },
    openProfile() { this.open = false; this.profileOpen = true; this._fillForm(this.player || {}); },
    openReset() { this.open = false; this.resetOpen = true; this.reset = { current: '', next: '', confirm: '' }; },

    // ---------- Logout ----------
    async logout() {
        this.open = false;
        try {
            if (typeof globalThis.AdminAuth?.logout === 'function') {
                await globalThis.AdminAuth.logout();
                return;
            }
        } catch {
            /* fall through */
        }
        try {
            await apiFetch('/admin/auth/logout', { method: 'POST' });
        } catch {
            /* ignore */
        }
        try {
            globalThis.localStorage?.removeItem('dartsos.admin.token');
        } catch {
            /* ignore */
        }
        this.session?.clear();
        globalThis.location.href = '/admin/login';
    },

    // ---------- Fetch current user ----------
    async fetchMe() {
        try {
            const res = await apiFetch('/auth/me');
            if (res.status === 204) {
                this.session.set({ me: null });
                return;
            }
            if (!res.ok) {
                if (res.status === 401) { this.session.set({ me: null }); return; }
                const err = await res.json().catch(() => ({}));
                throw new Error(err.message || `Failed (${res.status})`);
            }
            const text = await res.text();
            const data = text ? JSON.parse(text) : null;
            const p = data?.player && typeof data.player === 'object' ? data.player : {};
            const merged = {
                ...p,
                _id: p._id ?? data?._id ?? this.session?.me?._id,
                email: p.email ?? data?.email ?? this.session?.me?.email,
                role: p.role ?? data?.role ?? this.session?.me?.role,
                accountId: p.accountId ?? data?.accountId ?? this.session?.me?.accountId ?? null,
                playerId: p.playerId ?? data?.playerId ?? this.session?.me?.playerId ?? null,
                fullName: p.fullName ?? p.displayName ?? data?.fullName ?? this.session?.me?.fullName,
                displayName: p.displayName ?? p.fullName ?? data?.fullName ?? this.session?.me?.displayName,
                imageUrl: p.imageUrl ?? this.session?.me?.imageUrl ?? '',
            };
            this.session.set({ me: merged });
            this.syncAvatarSrc();
        } catch (e) {
            if (e?.name === 'AbortError') return;
            console.error('fetchMe error:', e);
        }
    },

    // ---------- Profile edit ----------
    _fillForm(p) {
        this.form = {
            fullName: p.fullName || '',
            nickname: p.nickname || '',
            email: p.email || '',
            phone: p.phone || '',
            status: p.status || 'active',
            imageUrl: p.imageUrl || ''
        };
    },

    onImageChange(e) {
        const f = e.target.files?.[0];
        this.imageFile = f || null;
        this.imagePreview = f ? URL.createObjectURL(f) : null;
    },

    async _presignAndUpload() {
        if (!this.imageFile) return null;

        // 1) Get presigned PUT URL from API
        const pres = await apiFetch('/players/presign', {
            method: 'POST',
            body: {
                filename: this.imageFile.name || 'profile.jpg',
                contentType: this.imageFile.type || 'image/jpeg',
            }
        });
        if (!pres.ok) {
            const err = await pres.json().catch(() => ({}));
            throw new Error(err.message || 'Failed to presign image');
        }
        const { url, publicUrl } = await pres.json();

        // 2) Upload directly to storage (NOT apiFetch)
        const put = await fetch(url, {
            method: 'PUT',
            body: this.imageFile,
            headers: { 'Content-Type': this.imageFile.type || 'image/jpeg' }
        });
        if (!put.ok) throw new Error('Upload failed');

        return publicUrl;
    },

    async saveProfile() {
        const p = this.player;
        const rosterId = String(p?.playerId || p?._id || '').trim();
        if (!rosterId) {
            alert('No league roster player is linked to this sign-in. Turn on “Participate as a player” in Account settings → My profile, or ask an admin to link your portal player.');
            return;
        }

        this.saving = true;
        try {
            // Optional image upload
            if (this.imageFile) {
                const url = await this._presignAndUpload();
                if (url) this.form.imageUrl = url;
            }

            // Only allowed fields
            const payload = {
                fullName: this.form.fullName,
                nickname: this.form.nickname || null,
                email: this.form.email || null,
                phone: this.form.phone || null,
                status: this.form.status,
                ...(this.form.imageUrl ? { imageUrl: this.form.imageUrl } : {})
            };

            const r = await apiFetch(`/players/${encodeURIComponent(rosterId)}`, { method: 'PATCH', body: payload });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) {
                alert(data.message || 'Save failed');
                return;
            }

            // Merge into session.me
            const updated = unwrapApiEntity(data, ['player', 'item']) || data;
            const me = this.session.me || {};
            if (me.player) {
                this.session.set({ me: { ...me, player: { ...me.player, ...updated } } });
            } else {
                this.session.set({ me: { ...me, ...updated } });
            }
            this.syncAvatarSrc();

            this.profileOpen = false;
            this.imageFile = null;
            this.imagePreview = null;
        } catch (e) {
            alert(e.message || 'Error saving profile');
        } finally {
            this.saving = false;
        }
    },

    // ---------- Password reset ----------
    reset: { current: '', next: '', confirm: '' },

    async doReset() {
        if (!this.reset.next || !this.reset.confirm) {
            alert('Please fill all fields'); return;
        }
        if (this.reset.next !== this.reset.confirm) {
            alert('Passwords do not match'); return;
        }

        this.resetting = true;
        try {
            const r = await apiFetch('/auth/reset', {
                method: 'POST',
                body: { newPassword: this.reset.next }
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) {
                this.$store.toastr.error(data.message || 'Password reset failed')
                return;
            }
            this.resetOpen = false;
            // (Optional) clear fields
            this.reset = { current: '', next: '', confirm: '' };
            this.$store.toastr.success('Password updated');
        } catch (e) {
            this.$store.toastr.error(e?.message || 'Password reset failed')
        } finally {
            this.resetting = false;
        }
    }

}));
